// @refresh reset
import React, { createContext, useState, useEffect, useContext, useCallback, ReactNode } from 'react';
import { useGoogleOneTapLogin } from '@react-oauth/google';

declare global {
  interface Window {
    google?: { accounts?: { id?: { revoke?: (email: string, cb: () => void) => void } } };
  }
}
import { useNavigate, useLocation } from 'react-router-dom';
import { clearSyncState } from '../services/queueService';
import { AuthUser } from '../types/auth';
import { fetchMeAsync } from '../services/api/auth';
import { ONE_TAP_GRACE_MS } from '../constants/auth';
import {
  setIdToken,
  getIdToken,
  isTokenExpired,
  wasAuthenticated,
  getCachedProfile,
  persistAuthState,
  clearAuthState,
  getEmailFromToken,
} from '../services/googleAuthSession';

import type { AuthContextType } from '../interfaces/auth-context';

const AuthContext = createContext<AuthContextType>({
  user: null,
  idToken: null,
  isAuthenticated: false,
  isLoading: true,
  authError: null,
  loginWithGoogleJwt: async () => {},
  triggerSilentLogin: () => {},
  logout: async () => {},
});

// Eigene Komponente, damit useGoogleOneTapLogin bei jedem triggerSilentLogin()-Aufruf
// (per `key`-Wechsel am Aufrufer) neu gemountet wird und einen frischen Prompt-Zyklus startet -
// der Hook selbst bietet keine imperative "jetzt erneut versuchen"-Funktion.
const GoogleOneTapListener: React.FC<{ onSuccess: (token: string) => void; disabled: boolean }> = ({
  onSuccess,
  disabled,
}) => {
  useGoogleOneTapLogin({
    onSuccess: (r) => r.credential && onSuccess(r.credential),
    disabled,
    auto_select: true,
    use_fedcm_for_prompt: false,
  });
  return null;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [idToken, setIdTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<'wrong-account' | 'logged-out' | null>(null);
  const [silentLoginTrigger, setSilentLoginTrigger] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const triggerSilentLogin = () => setSilentLoginTrigger(n => n + 1);

  const loginWithGoogleJwt = useCallback(async (token: string): Promise<void> => {
    try {
      const profile = await fetchMeAsync(token);
      setIdTokenState(token);
      setIdToken(token);
      setUser(profile);
      setAuthError(null);
      setIsLoading(false);
      persistAuthState(profile);
      if (location.pathname === '/login' || location.pathname === '/') {
        navigate('/projects');
      }
    } catch (err: any) {
      console.error('[Auth] loginWithGoogleJwt fehlgeschlagen:', err);
      if (err?.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:wrong-account'));
      }
    }
  }, [navigate]);

  useEffect(() => {
    // Gecachtes Profil nur sofort übernehmen, wenn auch ein gültiger Token im
    // sessionStorage liegt - der Token wird pro Tab gehalten (sessionStorage) und ist
    // nach Reload/neuem Tab oft (noch) leer, während das Profil (localStorage) bestehen
    // bleibt. Ein abgelaufener Token wird ebenfalls verworfen, da er sonst kurz als
    // "eingeloggt" durchgeht und erst nach einem fehlgeschlagenen API-Call (401 →
    // auth:unauthorized → Redirect auf /login) korrigiert wird - sichtbarer Flicker.
    const sessionToken = getIdToken();
    if (wasAuthenticated() && sessionToken && !isTokenExpired(sessionToken)) {
      const cached = getCachedProfile();
      if (cached) setUser(cached);
      setIdTokenState(sessionToken);
      setIsLoading(false);
      return;
    }
    // Kein nutzbarer Token: One Tap dieselbe Gnadenfrist wie in LoginScreen.tsx geben,
    // bevor isLoading auf false springt - sonst leitet AuthGuard auf /login um und
    // Komponenten feuern API-Calls, während der One-Tap-Credential-Austausch noch läuft.
    const timeout = window.setTimeout(() => setIsLoading(false), ONE_TAP_GRACE_MS);
    return () => window.clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const handleWrongAccount = () => {
      const email = getEmailFromToken(idToken);
      if (email) window.google?.accounts?.id?.revoke?.(email, () => {});
      setAuthError('wrong-account');
      setIdTokenState(null);
      setIdToken(null);
    };
    const handleUnauthorized = () => {
      clearAuthState();
      setUser(null);
      setIdTokenState(null);
      setIdToken(null);
    };
    const handleOnline = () => triggerSilentLogin();

    window.addEventListener('auth:wrong-account', handleWrongAccount);
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('auth:wrong-account', handleWrongAccount);
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('online', handleOnline);
    };
  }, [idToken]);

  const logout = (): void => {
    setIdTokenState(null);
    setIdToken(null);
    setUser(null);
    setAuthError('logged-out');
    clearAuthState();
    clearSyncState();
    navigate('/login');
  };

  const contextValue: AuthContextType = {
    user,
    idToken,
    isAuthenticated: !!user,
    isLoading,
    authError,
    loginWithGoogleJwt,
    triggerSilentLogin,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {/* key erzwingt Remount bei triggerSilentLogin() (z.B. nach 'online') für einen frischen Prompt-Zyklus.
          disabled hängt bewusst NICHT von idToken ab, damit der Token auch während einer aktiven Session
          still im Hintergrund erneuert wird. */}
      <GoogleOneTapListener
        key={silentLoginTrigger}
        onSuccess={loginWithGoogleJwt}
        disabled={!!authError}
      />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
